import classNames from 'classnames';
import { CSSProperties, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import {
  mobileAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

const delayAfterFirstStep = 500;
const delayBeforeFirstHalo = 400;
const delayBeforeSecondHalo = 600;
const haloAnimationDuration = 500;
const dimAnimationDuration = 300;

const appear = keyframes`
    from {
        opacity: 0;
        transform: scale(1.2);
    }
    20% {
      opacity: 1;
      transform: scale(1.18);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;
const brightnessAnimation = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
const glowAppear = keyframes`
    0% {
        opacity: 0;
        transform: scale(0.5);
    }
    100% {
        opacity: 0.5;
        transform: scale(1.2);
    }
`;
const haloAnimation = keyframes`
    0% {
        opacity: 0;
        transform: scale(0.5);
    }
    50% {
        opacity: 1;
        transform: scale(1.2);
    }
    100% {
        opacity: 0;
        transform: scale(1.2);
    }
`;
const highlight = keyframes`
    0%{
        transform: scale(1);
    }
    20% {

        transform: scale(0.85);
    }
    100% {
        transform: scale(1.3);
    }
`;

const Root = styled.div`
  width: calc(28 * var(--unit));
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--unit);
  position: relative;

  @media ${mobileAndAbove} {
    width: calc(40 * var(--unit));
  }

  @media ${tabletAndAbove} {
    width: calc(60 * var(--unit));
  }
`;
const ImgWrapper = styled.div`
  position: relative;
  opacity: 0;
  transform: scale(1.2);
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  will-change: transform, opacity;
  animation: ${appear} 200ms forwards var(--animation-delay);

  &.highlight {
    opacity: 1;
    transform: scale(1);
    animation: ${highlight} 1000ms forwards ${delayAfterFirstStep}ms;
    z-index: 2;
  }
`;
const HighlightGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: var(--double-unit);
  z-index: -2;
  background-color: var(--c-yellow-500);
  filter: blur(var(--double-unit));
  transform: scale(0.5);
  opacity: 0;
  animation: ${glowAppear} 300ms forwards ${delayAfterFirstStep}ms;
`;
const Halo = styled.div`
  position: absolute;
  inset: 0;
  border-radius: var(--unit);
  z-index: -1;
  border: 1px solid var(--c-yellow-400);
  transform: scale(0.5);
  opacity: 0;
  animation: ${haloAnimation} ${haloAnimationDuration}ms forwards
    ${delayAfterFirstStep + delayBeforeFirstHalo}ms;
`;
const BlurredHalo = styled(Halo)`
  filter: blur(2px);
`;
const CrispHalo = styled(Halo)`
  border-width: 2px;
  filter: drop-shadow(0 0 4px var(--c-yellow-500));
`;
const BlurredHalo2 = styled(BlurredHalo)`
  animation: ${haloAnimation} ${haloAnimationDuration}ms forwards
    ${delayAfterFirstStep + delayBeforeSecondHalo}ms;
`;
const CrispHalo2 = styled(CrispHalo)`
  animation: ${haloAnimation} ${haloAnimationDuration}ms forwards
    ${delayAfterFirstStep + delayBeforeSecondHalo}ms;
`;
const PlayerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const PlayerGlowImg = styled(PlayerImg)`
  position: absolute;
  inset: 0;
  filter: brightness(1.5) drop-shadow(0 0 2px var(--c-yellow-500));
  animation: ${brightnessAnimation} 400ms forwards var(--animation-delay);
`;
const DimmedLayer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  border-radius: 4%;
  transition: opacity ${dimAnimationDuration}ms ${delayAfterFirstStep}ms;
  &.appear {
    opacity: 1;
  }
`;
const players = [
  'https://assets.sorare.com/image-resize/card/8d87dda4-76d9-4d2e-9537-aea93e3e6cda/picture/tinified-a8b89eb6d1d2b2cb1e526d27bb10b78d.png?width=320&xl=true',
  'https://assets.sorare.com/image-resize/card/b86da81c-098b-4719-92ec-761bc56f8a4d/picture/tinified-91faed4605fb43daec66bde97741da39.png?width=320&xl=true',
  'https://assets.sorare.com/image-resize/card/460f5159-48cc-4640-9469-a9bcaf9d6fac/picture/tinified-dbb3f41445ef0e51bb67aa812a57ea15.png?width=320&xl=true',
  'https://assets.sorare.com/image-resize/card/712cda7d-3a2e-4e46-9768-2cf65b352b8b/picture/tinified-027ef01ba691ff9a17e800e9554b3c2c.png?width=320&xl=true',
  'https://assets.sorare.com/image-resize/card/ae29a762-ace5-4e58-abf2-fcc4834608c7/picture/tinified-22a4df3d31d5a51cf259876db4b93df1.png?width=640&xl=true',
  'https://assets.sorare.com/image-resize/card/d2ffa4ca-7a39-4c56-8fde-069ac59f3e52/picture/tinified-05c141d9bec06f42d284907bb4a556da.png?width=320&xl=true',
  'https://assets.sorare.com/image-resize/card/3a973cae-7962-4adb-8019-a052d0ea5d4b/picture/tinified-534804303cc74860590caa75d87a7e55.png?width=320&xl=true',
  'https://assets.sorare.com/image-resize/card/9efb5008-10f3-44e7-9263-4c03b56ba776/picture/tinified-407a97ccd23306994245fe7dc9d8f304.png?width=320&xl=true',
  'https://assets.sorare.com/image-resize/card/c6ca0386-df30-4c12-abe6-ec8c08f19491/picture/tinified-89f78996a5b8f91f033c8724de85b48f.png?width=320&xl=true',
];

const centralImageIndex = 4;

export const RecruitPlayersAnimation = () => {
  const [firstStepDone, setFirstStepDone] = useState(false);

  return (
    <Root>
      {players.map((player, index) => {
        const highlightCard = index === centralImageIndex && firstStepDone;
        const dimCard = index !== centralImageIndex && firstStepDone;

        return (
          <ImgWrapper
            key={player}
            className={classNames({
              highlight: highlightCard,
            })}
            style={
              {
                '--animation-delay': `${index * 70}ms`,
              } as CSSProperties
            }
            onAnimationEnd={
              index === players.length - 1
                ? () => {
                    setFirstStepDone(true);
                  }
                : undefined
            }
          >
            {!!highlightCard && (
              <>
                <HighlightGlow />
                <BlurredHalo />
                <CrispHalo />
                <BlurredHalo2 />
                <CrispHalo2 />
              </>
            )}
            <PlayerImg src={player} alt="" />
            <PlayerGlowImg src={player} alt="" />
            <DimmedLayer className={classNames({ appear: dimCard })} />
          </ImgWrapper>
        );
      })}
    </Root>
  );
};
